import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    input,
    OnDestroy,
    Renderer2,
    signal,
    ViewEncapsulation,
    inject,
    viewChild,
} from '@angular/core';
import { isDefined } from '../utils';

export interface IGradientColor {
    lightColor: string;
    darkColor: string;
}

@Component({
    selector: 'fi-gradient-circle',
    templateUrl: './fi-gradient-circle.component.html',
    styleUrls: ['./fi-gradient-circle.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-gradient-circle' },
    standalone: false,
})
export class FiGradientCircleComponent implements OnDestroy {
    private static nextGradientColorElementId = 0;

    public fillPercentage = input<number>(0); // 0-100
    public lightColor = input<string>('#2196F3');
    public darkColor = input<string>('#2196F3');
    public size = input<number>(175); // Size in pixels (width and height)
    public strokeWidth = input<number>(18);
    public animationDuration = input<number>(200); // Animation duration in milliseconds, 0 means no animation
    public fillDirection = input<'clockwise' | 'anticlockwise'>('clockwise');
    public fullFillLightColor = input<string>('#2196F3');
    public fullFillDarkColor = input<string>('#2196F3');

    private readonly circleRef = viewChild<ElementRef<SVGCircleElement>>('circleRef');
    private readonly wrapperRef = viewChild<ElementRef<HTMLElement>>('wrapperRef');
    private readonly svgRef = viewChild<ElementRef<SVGElement>>('svgRef');
    private readonly linearGradientRef = viewChild<ElementRef<SVGLinearGradientElement>>('linearGradientRef');

    private get circleEl(): SVGCircleElement | undefined {
        return this.circleRef()?.nativeElement;
    }

    private get wrapperEl(): HTMLElement | undefined {
        return this.wrapperRef()?.nativeElement;
    }

    private get svgEl(): SVGElement | undefined {
        return this.svgRef()?.nativeElement;
    }

    private get linearGradientEl(): SVGLinearGradientElement | undefined {
        return this.linearGradientRef()?.nativeElement;
    }

    protected readonly gradientLightColor = computed(() => {
        return this.fillPercentage() === 100 ? this.fullFillLightColor() : this.lightColor();
    });

    protected readonly gradientDarkColor = computed(() => {
        return this.fillPercentage() === 100 ? this.fullFillDarkColor() : this.darkColor();
    });

    private readonly radius = computed(() => {
        const sizeValue = this.size();
        const strokeWidthValue = this.strokeWidth();
        return (sizeValue - strokeWidthValue) / 2;
    });

    private readonly circumferenceFiller = computed(() => {
        const r = this.radius();
        return r > 0 ? Math.round(r * 2 * Math.PI) : 0;
    });

    private readonly strokeOffset = computed(() => {
        const fillPercent = Math.max(0, Math.min(100, this.fillPercentage()));
        const circumference = this.circumferenceFiller();
        return circumference > 0 ? Math.round((circumference * (100 - fillPercent)) / 100) : 0;
    });

    private readonly svgTransform = computed(() => {
        return this.fillDirection() === 'clockwise' ? 'scale(-1, 1) rotate(-90deg)' : 'rotate(-90deg)';
    });

    private readonly isFirstSet = signal<boolean>(true);
    private readonly currentTargetOffset = signal<number | null>(null);
    private readonly currentActualOffset = signal<number | null>(null);

    private readonly gradientColorElementId: string = `gradientCircleColor-${FiGradientCircleComponent.nextGradientColorElementId++}`;
    private readonly circleStrokeDashoffsetStyleVar: string = '--circle-stroke-dashoffset';
    private readonly initialCircleStrokeDashoffsetStyleVar: string = '--initial-circle-stroke-dashoffset';
    private readonly circleStrokeAnimationTimeStyleVar: string = '--circle-stroke-animation-time';

    private animationEndUnlisten: (() => void) | null = null;

    private readonly renderer = inject(Renderer2);
    private readonly elementRef = inject(ElementRef);

    private readonly circleInitEffect = effect(() => {
        const circle = this.circleEl;
        if (isDefined(circle) && !this.animationEndUnlisten) {
            this.setupAnimationEndListener();
        }
    });

    private readonly svgTransformEffect = effect(() => {
        const transform = this.svgTransform();
        const svg = this.svgEl;

        if (isDefined(svg)) {
            this.renderer.setStyle(svg, 'transform', transform);
        }
    });

    private readonly circleSizeEffect = effect(() => {
        const radius = this.radius();
        this.updateCircleSize();
    });

    private readonly strokeOffsetEffect = effect(() => {
        const strokeOffsetValue = this.strokeOffset();
        const circumference = this.circumferenceFiller();
        const circle = this.circleEl;

        if (isDefined(circle) && circumference > 0) {
            this.currentTargetOffset.set(strokeOffsetValue);
            const currentActual = this.currentActualOffset();
            if (currentActual === null) {
                this.currentActualOffset.set(circumference);
            }
            this.updateStrokeDashoffset(strokeOffsetValue, circumference);
        }
    });

    private updateCircleSize(): void {
        const wrapper = this.wrapperEl;
        const circle = this.circleEl;
        const gradient = this.linearGradientEl;

        if (!isDefined(wrapper) || !isDefined(circle) || !isDefined(gradient)) {
            return;
        }

        const radiusValue = this.radius();
        const circumference = this.circumferenceFiller();
        const strokeWidthValue = this.strokeWidth();
        const sizeValue = this.size();
        const strokeOffsetValue = this.strokeOffset();

        const strokeWidth = circumference - strokeOffsetValue > 0 ? strokeWidthValue : 0;

        this.renderer.setStyle(circle, 'stroke-width', strokeWidth + 'px');
        this.renderer.setStyle(circle, 'stroke-dasharray', `${circumference}`);
        this.renderer.setStyle(circle, 'stroke', `url(#${this.gradientColorElementId})`);

        this.renderer.setAttribute(gradient, 'id', this.gradientColorElementId);

        const center = sizeValue / 2;
        this.renderer.setAttribute(circle, 'r', `${radiusValue}`);
        this.renderer.setAttribute(circle, 'cx', `${center}`);
        this.renderer.setAttribute(circle, 'cy', `${center}`);

        if (circumference > 0) {
            this.updateStrokeDashoffset(strokeOffsetValue, circumference);
        }
    }

    private updateStrokeDashoffset(strokeOffset: number, circumference: number): void {
        const circle = this.circleEl;
        if (!isDefined(circle)) {
            return;
        }

        const animationDurationValue = this.animationDuration();
        const isFirstSetValue = this.isFirstSet();
        const wrapper = this.wrapperEl;

        const currentOffset: number = this.currentActualOffset() ?? circumference;

        if (animationDurationValue > 0 && isFirstSetValue) {
            this.currentActualOffset.set(circumference);
            this.setStrokeDashoffset(circumference);
            this.elementRef.nativeElement.style.setProperty(
                this.initialCircleStrokeDashoffsetStyleVar,
                circumference.toString(),
            );
            this.elementRef.nativeElement.style.setProperty(
                this.circleStrokeDashoffsetStyleVar,
                strokeOffset.toString(),
            );
            this.elementRef.nativeElement.style.setProperty(
                this.circleStrokeAnimationTimeStyleVar,
                `${animationDurationValue}ms`,
            );

            if (isDefined(wrapper)) {
                this.renderer.addClass(wrapper, 'animated');
            }

            circle.classList.add('visible');
            this.isFirstSet.set(false);
        } else if (animationDurationValue > 0 && !isFirstSetValue) {
            if (isDefined(wrapper)) {
                this.renderer.removeClass(wrapper, 'animated');
            }

            circle.classList.add('visible');

            setTimeout(() => {
                this.elementRef.nativeElement.style.setProperty(
                    this.initialCircleStrokeDashoffsetStyleVar,
                    currentOffset.toString(),
                );
                this.elementRef.nativeElement.style.setProperty(
                    this.circleStrokeDashoffsetStyleVar,
                    strokeOffset.toString(),
                );
                this.elementRef.nativeElement.style.setProperty(
                    this.circleStrokeAnimationTimeStyleVar,
                    `${animationDurationValue}ms`,
                );

                this.setStrokeDashoffset(currentOffset);

                if (isDefined(wrapper)) {
                    this.renderer.addClass(wrapper, 'animated');
                }
            }, 10);
        } else {
            if (isDefined(wrapper)) {
                this.renderer.removeClass(wrapper, 'animated');
            }
            this.currentActualOffset.set(strokeOffset);
            this.setStrokeDashoffset(strokeOffset);
            circle.classList.add('visible');
            if (isFirstSetValue) {
                this.isFirstSet.set(false);
            }
        }
    }

    private setStrokeDashoffset(strokeOffset: number): void {
        const circle = this.circleEl;
        if (!isDefined(circle)) {
            return;
        }
        circle.style.strokeDashoffset = strokeOffset.toString();
    }

    private setupAnimationEndListener(): void {
        const circle = this.circleEl;
        if (!isDefined(circle) || this.animationEndUnlisten) {
            return;
        }

        const animationEndHandler = () => {
            const currentCircle = this.circleEl;
            if (!isDefined(currentCircle)) {
                return;
            }

            const targetOffset = this.currentTargetOffset();
            if (targetOffset !== null) {
                this.currentActualOffset.set(targetOffset);
                currentCircle.style.strokeDashoffset = targetOffset.toString();
            }

            const wrapper = this.wrapperEl;
            if (isDefined(wrapper)) {
                this.renderer.removeClass(wrapper, 'animated');
            }
        };

        this.animationEndUnlisten = this.renderer.listen(circle, 'animationend', animationEndHandler);
    }

    public ngOnDestroy(): void {
        if (this.animationEndUnlisten) {
            this.animationEndUnlisten();
            this.animationEndUnlisten = null;
        }
    }
}
